import React from "react";
import {
  Reveal,
  MaskLine,
  SectionMark,
  GridLines,
  RegMarks,
  Glass,
  Shell,
} from "../Components/Primitives";
import Contact from "../Components/Contact";
import Seo, { SITE } from "../Components/Seo";
import Footer from "./Footer";
import { projects } from "../data/projects";

/* Built once at module scope so the reference stays stable across
   renders and Seo does not re-inject the tag on every pass. */
const SEO = {
  title: "Selected Work - Projects by Timi Gbenga",
  description:
    "The full project index of Timi Gbenga: myQuest, Adools Construct & Designs, EcoPack and Disena Creates. React, Next.js, React Native, Three.js and GSAP builds shipped from Lagos, Nigeria.",
  path: "/projects",
  schema: {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Selected Work - Projects by Timi Gbenga",
    url: `${SITE}/projects`,
    author: { "@id": `${SITE}/#person` },
    mainEntity: {
      "@type": "ItemList",
      numberOfItems: projects.length,
      itemListElement: projects.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: p.title,
        url: p.link,
        description: p.summary,
      })),
    },
  },
};

/* Strip the protocol and any www. so the plate caption reads as a
   place you could type into a browser. */
const hostOf = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
};

/* Full-bleed entry. Plates alternate sides so the eye walks
   the page rather than scanning a uniform grid. */
const Entry = ({ project, i }) => {
  const flip = i % 2 === 1;

  return (
    <article className="border-b border-rule py-14 md:py-24">
      <div
        className={[
          "grid gap-10 lg:grid-cols-2 lg:gap-16 lg:items-center",
          flip ? "lg:[&>*:first-child]:order-2" : "",
        ].join(" ")}
      >
        {/* Plate */}
        <Reveal delay={40}>
          <Glass className="relative rounded-[26px] p-3">
            <RegMarks inset={11} />
            <div className="relative aspect-[16/11] overflow-hidden rounded-[18px] bg-ground-sunk">
              <img
                src={project.image}
                alt={`${project.title} interface`}
                loading="lazy"
                className="h-full w-full object-cover object-top transition-transform duration-[1.4s] ease-glass hover:scale-[1.03]"
              />
            </div>
            <div className="flex items-baseline justify-between gap-4 px-2 pb-1 pt-3.5">
              <span className="label">{project.discipline}</span>
              <span className="label truncate">{hostOf(project.link)}</span>
            </div>
          </Glass>
        </Reveal>

        {/* Record */}
        <div>
          <Reveal className="flex items-center gap-4 pb-7">
            <span className="h-px w-10 bg-[var(--rule-2)]" />
            <span className="label">{project.year}</span>
          </Reveal>

          <Reveal delay={80}>
            <h2 className="display display-md">{project.title}</h2>
          </Reveal>

          <Reveal delay={140}>
            <p className="lede max-w-lg pt-6">{project.summary}</p>
          </Reveal>

          {project.metrics && (
            <Reveal
              delay={170}
              className="flex flex-wrap items-baseline gap-x-10 gap-y-3 pt-7"
            >
              {project.metrics.map((m) => (
                <span key={m.k} className="flex items-baseline gap-2.5">
                  <span className="text-[24px] font-medium tracking-[-0.03em] tabular-nums">
                    {m.v}
                  </span>
                  <span className="label">{m.k}</span>
                </span>
              ))}
            </Reveal>
          )}

          <Reveal delay={200} className="pt-9">
            <dl className="border-t border-rule">
              <div className="flex items-baseline justify-between gap-6 border-b border-rule py-3.5">
                <dt className="label">Role</dt>
                <dd className="text-[14px] tracking-[-0.01em]">
                  {project.role}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-6 border-b border-rule py-3.5">
                <dt className="label">Discipline</dt>
                <dd className="text-[14px] tracking-[-0.01em]">
                  {project.discipline}
                </dd>
              </div>
              <div className="flex items-start justify-between gap-6 border-b border-rule py-3.5">
                <dt className="label pt-1">Stack</dt>
                <dd className="flex flex-wrap justify-end gap-1.5">
                  {project.stack.map((s) => (
                    <span
                      key={s}
                      className="mono rounded-full border border-rule-2 px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] text-ink-70"
                    >
                      {s}
                    </span>
                  ))}
                </dd>
              </div>
            </dl>
          </Reveal>

          <Reveal delay={260} className="flex flex-wrap items-center gap-6 pt-8">
            <a
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 rounded-full bg-ink px-6 py-3 text-[13px] font-medium text-ground-raise transition-opacity duration-500 hover:opacity-85"
            >
              Visit live
              <span className="transition-transform duration-500 ease-glass group-hover:translate-x-1">
                &rarr;
              </span>
            </a>

            {project.github && (
              <a
                href={project.github}
                target="_blank"
                rel="noopener noreferrer"
                className="link-draw text-[13.5px] font-medium text-ink-70 hover:text-ink transition-colors duration-500"
              >
                Source
              </a>
            )}
          </Reveal>
        </div>
      </div>
    </article>
  );
};

const Work = () => (
  <main className="relative">
    <Seo {...SEO} />
    <GridLines count={5} />

    <div className="relative z-10">
      <section className="pt-32 md:pt-44 pb-10 md:pb-16">
        <Shell>
          <Reveal className="flex items-center gap-4 pb-10 md:pb-14">
            <span className="h-px w-10 bg-[var(--rule-2)]" />
            <span className="label">Index of Work</span>
          </Reveal>

          <h1 className="display display-lg">
            <MaskLine delay={60}>Selected</MaskLine>
            <MaskLine delay={160}>
              <span className="text-ink-45">Projects</span>
            </MaskLine>
          </h1>

          <Reveal delay={320} className="pt-8 md:pt-12">
            <p className="lede max-w-xl">
              Applications and platforms built over the last two years. Each
              one shipped, each one still running.
            </p>
          </Reveal>
        </Shell>
      </section>

      <Shell>
        <SectionMark title="The Index" meta={`${projects.length} entries`} />

        <div className="border-t border-rule">
          {projects.map((p, i) => (
            <Entry key={p.id} project={p} i={i} />
          ))}
        </div>
      </Shell>

      <Contact />
      <Footer />
    </div>
  </main>
);

export default Work;
